import "./CSS/full-screen-popup.scss";

import {
  AppType,
  Channel,
  DialogType,
  SessionStorageItems,
  Topic
} from "../../constants";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { UISelectors, UserContextSelectors } from "../../redux/selectors";

import { FullScreenPopupBody } from "./FullScreenPopupBody";
import { FullScreenPopupDemoButton } from "./FullScreenPopupDemoButton";
import GameHeader from "./GameHeader";
import { MessageBrokerUtils } from "../../utils";
import classNames from "classnames";
import { useNoScroll } from "../../hooks";
import { useSelector } from "react-redux";
import { useAppContainerReady } from "../../hooks/useAppContainerReady";

const FullScreenPopup = () => {
  const gameFrameRef = useRef(null);
  const fullScreenPopup = useSelector(UISelectors.fullScreenPopup);
  const isMobile = useSelector(UserContextSelectors.isMobile);
  const config = useSelector(UISelectors.config);
  const [isLoading, setIsLoading] = useState(true);

  useAppContainerReady(fullScreenPopup);

  const visible = !!fullScreenPopup;
  const gameHeaderVisible =
    fullScreenPopup?.appType === AppType.Game && !isMobile;
  const buttonVisible =
    sessionStorage.getItem(SessionStorageItems.ShowDevTools) === "true";

  const className = useMemo(
    () =>
      classNames("full-screen-popup", { "full-screen-popup-show": visible }),
    [visible]
  );

  const onClose = useCallback(() => {
    MessageBrokerUtils.publish({
      channel: Channel.SESSION,
      topic: Topic.appClosed,
      headers: {
        publisher: "UAA"
      },
      data: {
        appID: fullScreenPopup.appID,
        correlationID: fullScreenPopup.correlationID
      }
    });
  }, [fullScreenPopup]);

  useEffect(() => {
    let appInitSucceededSub;
    if (!!fullScreenPopup) {
      appInitSucceededSub = MessageBrokerUtils.subscribe({
        channel: Channel.SESSION,
        topic: Topic.appInitSucceeded,
        callback: data => {
          if (
            data?.appID !== undefined &&
            data.appID === fullScreenPopup.appID
          ) {
            setIsLoading(false);
          }
          appInitSucceededSub?.unsubscribe();
        }
      });
      if (fullScreenPopup.error) {
        MessageBrokerUtils.publish({
          channel: Channel.UI,
          topic: Topic.Dialog,
          headers: {
            publisher: "UAA"
          },
          data: {
            dialogType: DialogType.Restriction,
            options: {
              onClose
            }
          }
        });
        setIsLoading(false);
      }
    }
    return () => {
      appInitSucceededSub?.unsubscribe();
      setIsLoading(true);
    };
  }, [fullScreenPopup, isMobile, onClose]);

  useNoScroll("fullScreenpopup", visible);

  let conditionalRender;
  if (fullScreenPopup && fullScreenPopup.appID !== undefined) {
    conditionalRender = (
      <FullScreenPopupBody containerID={fullScreenPopup.containerID} />
    );
  }

  return (
    <>
      {buttonVisible && <FullScreenPopupDemoButton />}
      <div className={className} ref={gameFrameRef}>
        {gameHeaderVisible && (
          <GameHeader
            gameLoading={isLoading}
            gameName={fullScreenPopup?.gameName}
            icons={config.gameHeaderIcons}
            forwardedRef={gameFrameRef}
            appID={fullScreenPopup?.appID}
            correlationID={fullScreenPopup?.correlationID}
          />
        )}
        {conditionalRender}
      </div>
    </>
  );
};

export default FullScreenPopup;
